import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { handleLogout } from "../api/logout";
import Logo from "../assets/images/Logo.png";
import { EnumSection, enumsectionToI18nKey } from "../generated/enums";
import { useAccountStorage } from "../hooks/useAccountStorage";
import { usePendingPartnershipCount } from "../hooks/usePartnerships";
import { isAssistant, Space, SPACE_META, spaceForType } from "../utils/roles";
import { Avatar } from "./common/Avatar";
import {
  isProfilePath,
  PROFILE_ACCOUNT_PATH,
  PROFILE_NAV_ITEMS,
} from "./screens/profile/profileNav";
import { sectionToIcon } from "./type/default";

interface NavEntry {
  section: EnumSection;
  link: string;
  /** Masquée pour les collaborateurs (*_assistant) : le backend leur refuse ces actions. */
  ownerOnly?: boolean;
}

const NAV_ENTRIES: Record<Space, NavEntry[]> = {
  admin: [
    { section: "section_dashboard", link: "/admin" },
    { section: "section_devices", link: "/admin/devices" },
    { section: "section_accounts", link: "/admin/users" },
    { section: "section_history", link: "/admin/remote-connections" },
  ],
  breeder: [
    { section: "section_dashboard", link: "/breeder" },
    { section: "section_regulators", link: "/breeder/device/me" },
    { section: "section_analysis", link: "/breeder/recharts" },
    { section: "section_breedings", link: "/breeder/breedings" },
    {
      section: "section_partnerships",
      link: "/breeder/partnerships",
      ownerOnly: true,
    },
    {
      section: "section_collaborators",
      link: "/breeder/collaborators",
      ownerOnly: true,
    },
  ],
  installer: [
    { section: "section_dashboard", link: "/installer" },
    { section: "section_breedings", link: "/installer/breedings" },
    {
      section: "section_partnerships",
      link: "/installer/partnerships",
      ownerOnly: true,
    },
    {
      section: "section_collaborators",
      link: "/installer/collaborators",
      ownerOnly: true,
    },
    { section: "section_history", link: "/installer/history" },
  ],
  group: [{ section: "section_dashboard", link: SPACE_META.group.home }],
};

// navItemsForSpace : entrées de nav selon l'espace. `accountType` distingue les
// collaborateurs (*_assistant), qui partagent l'espace de leur type principal mais pas
// toutes ses entrées.
export const navItemsForSpace = (space: Space, accountType?: string | null) =>
  NAV_ENTRIES[space]
    .filter((entry) => !(entry.ownerOnly && isAssistant(accountType)))
    .map((entry) => ({
      icon: <FontAwesomeIcon icon={sectionToIcon[entry.section]} />,
      text: entry.section,
      link: entry.link,
    }));

// Les libellés restent rendus et s'effacent : les retirer du DOM les faisait apparaître d'un
// coup, et passer à la ligne pendant que la barre s'élargissait. À l'ouverture, le fondu
// attend que la barre ait commencé à s'élargir ; à la fermeture, il part tout de suite.
const labelFade = (isOpen: boolean) =>
  `whitespace-nowrap transition-opacity motion-reduce:transition-none ${
    isOpen ? "opacity-100 duration-300 delay-100" : "opacity-0 duration-150"
  }`;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccountStorage } = useAccountStorage();
  const accountStorage = getAccountStorage();
  const space = spaceForType(accountStorage?.type);
  const { t } = useTranslation();
  // Chargé au montage de la navbar, qui ne se refait qu'au rechargement de la page : pas
  // de polling.
  // Le hook ne requête que pour les espaces qui ont l'entrée « Partenaires ».
  const { count: pendingCount } = usePendingPartnershipCount(space !== "admin");

  const isProfileSection = isProfilePath(location.pathname);

  const handleLogoutClick = () => {
    handleLogout(navigate);
  };

  return (
    <div className="h-screen p-3 sticky top-0 left-0">
      <div
        // Repliée, il reste 40px de contenu : la largeur d'un NavItem. Les icônes, alignées à
        // gauche, ne bougent donc pas pendant l'animation.
        className={`h-full bg-tertiary p-4 flex flex-col justify-between rounded-lg overflow-hidden ${
          isOpen ? "w-64" : "w-[4.5rem]"
        } transition-[width] duration-300 ease-in-out motion-reduce:transition-none cursor-pointer`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {/* Header */}
        <div
          className="flex items-center pb-4 border-b border-grey-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Toujours rendu pour garder la hauteur de l'en-tête ; rogné quand la barre se replie. */}
          <div
            className={`flex-1 min-w-0 overflow-hidden ${labelFade(isOpen)}`}
          >
            <img src={Logo} alt="Copernic" className="h-14 max-w-none" />
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="w-10 h-10 shrink-0 flex items-center justify-center text-secondary-text hover:text-secondary"
          >
            {isOpen ? (
              <FontAwesomeIcon icon="times" />
            ) : (
              <FontAwesomeIcon icon="bars" />
            )}
          </button>
        </div>

        {/* Navigation Links */}
        <nav id="tour-nav" onClick={(e) => e.stopPropagation()}>
          <ul className="space-y-10 text-center">
            {navItemsForSpace(space, accountStorage?.type).map((item) => (
              <NavItem
                key={item.link}
                id={`tour-nav-${item.text}`}
                icon={item.icon}
                text={item.text}
                isOpen={isOpen}
                active={location.pathname === item.link}
                link={item.link}
                badge={item.text === "section_partnerships" ? pendingCount : 0}
              />
            ))}
          </ul>
        </nav>

        {/* Footer avec profil */}
        {accountStorage && (
          <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="pt-4 border-t border-grey-medium w-full">
              <Link
                id="tour-nav-profile"
                to={PROFILE_ACCOUNT_PATH}
                className="flex items-center gap-3 overflow-hidden text-secondary-text hover:text-secondary"
              >
                <div className="w-10 h-10 shrink-0 bg-secondary-text rounded-lg overflow-hidden">
                  <Avatar
                    value={accountStorage.avatar}
                    firstName={accountStorage.first_name}
                    lastName={accountStorage.last_name}
                    emojiClassName="text-2xl"
                    initialsClassName="text-xl"
                  />
                </div>
                <div className={`min-w-0 ${labelFade(isOpen)}`}>
                  <p className="font-oxaniumMedium">
                    {accountStorage.first_name} {accountStorage.last_name}
                  </p>
                  <p className="text-sm text-grey-medium">
                    {accountStorage.company_name}
                  </p>
                </div>
              </Link>

              {/* Toujours rendu, replie hors du profil : la hauteur passe de 0fr a 1fr, ce
                  qui anime l'ouverture comme la fermeture sans connaitre la hauteur reelle.
                  `invisible` une fois replie sort les liens de l'ordre de tabulation. */}
              <div
                className={`grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-in-out motion-reduce:transition-none ${
                  isProfileSection
                    ? "grid-rows-[1fr] opacity-100 visible"
                    : "grid-rows-[0fr] opacity-0 invisible"
                }`}
              >
                <div className="overflow-hidden">
                  <ul
                    className={`pt-3 space-y-1 transition-[padding] duration-300 ease-in-out motion-reduce:transition-none ${
                      isOpen ? "pl-4" : "pl-0"
                    }`}
                  >
                    {PROFILE_NAV_ITEMS.map((item) => (
                      <li key={item.id}>
                        <Link
                          id={`nav-profile-${item.id}`}
                          to={item.link}
                          // Barre repliee : l'icone seule, le libelle passe au survol.
                          title={isOpen ? undefined : t(item.label)}
                          aria-label={isOpen ? undefined : t(item.label)}
                          // Meme gabarit que NavItem : p-2 autour d'une icone 6x6, donc un
                          // carre de 40px quand la barre est repliee.
                          className={`flex items-center p-2 rounded-lg overflow-hidden ${
                            location.pathname === item.link
                              ? "bg-secondary text-tertiary"
                              : "text-secondary-text hover:text-tertiary hover:bg-secondary transition-colors"
                          }`}
                        >
                          <div className="h-6 w-6 flex items-center justify-center">
                            <FontAwesomeIcon icon={item.icon} />
                          </div>
                          <span
                            className={`font-oxaniumMedium text-sm pl-3 ${labelFade(isOpen)}`}
                          >
                            {t(item.label)}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleLogoutClick();
              }}
              title={isOpen ? undefined : t("navbar-logout")}
              className="flex p-2 mt-3 items-center rounded-lg overflow-hidden text-secondary-text hover:text-red-600 transition-colors"
            >
              <div className="h-6 w-6 shrink-0 flex items-center justify-center">
                <FontAwesomeIcon icon="sign-out-alt" />
              </div>
              <span className={`font-oxaniumMedium pl-4 ${labelFade(isOpen)}`}>
                {t("navbar-logout")}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const NavItem = ({
  icon,
  text,
  isOpen,
  active,
  link = "#",
  badge = 0,
  id,
}: {
  icon: React.ReactNode;
  text: EnumSection;
  isOpen: boolean;
  active?: boolean;
  link?: string;
  /** Compteur affiché en pastille (0 = pas de pastille). */
  badge?: number;
  /** Ancre du tutoriel d'accueil (cf. components/tour). */
  id?: string;
}) => {
  const { t } = useTranslation();
  return (
    <li id={id}>
      <Link
        to={link}
        // overflow-hidden rogne le libellé pendant l'animation ; la pastille, ancrée à 8px du
        // bord, reste dans le cadre.
        className={`flex p-2 rounded-lg items-center overflow-hidden
        ${
          active
            ? "bg-secondary text-tertiary"
            : "text-secondary-text hover:text-tertiary hover:bg-secondary transition-colors"
        }`}
      >
        {/* La pastille est ancrée sur l'icône : c'est le seul élément visible quand la
            navbar est repliée (isOpen=false), où le libellé disparaît. */}
        <div className="h-6 w-6 shrink-0 relative">
          {icon}
          {badge > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[0.65rem] font-oxaniumMedium">
              {badge > 9 ? "9+" : badge}
            </span>
          )}
        </div>
        <span className={`font-oxaniumMedium pl-4 ${labelFade(isOpen)}`}>
          {t(enumsectionToI18nKey[text])}
        </span>
      </Link>
    </li>
  );
};

export default Navbar;
